import { useCallback, useEffect, useState } from 'react';
import './chartSetup'; // side-effect: registers Chart.js elements
import ProfileAnalyticsService from './profileAnalyticsService';
import { EntrepreneurAnalysisData, ProfileAnalyticsData } from './types';
import { analysisSchoolIds, campusQueryValue } from './directoryLinks';
import BreakupSection from './components/BreakupSection';
import SeniorityFunnelSection from './components/SeniorityFunnelSection';
import EntrepreneurAnalysisSection from './components/EntrepreneurAnalysisSection';
// import EngagementRatesSection from './components/EngagementRatesSection'; // hidden — see render below

interface InstituteOption {
    label: string;
    value: string;
}

interface Props {
    instituteId: string | number;
    // When provided, render this data instead of fetching (used by the upload preview).
    data?: ProfileAnalyticsData | null;
    // Bumping this triggers a re-fetch (e.g. after a successful upload).
    refreshKey?: number;
    embedded?: boolean;
    // Group/multi-campus selector (institute-admin side). When provided with >1 option,
    // a dropdown is shown in the header.
    instituteOptions?: InstituteOption[];
    selectedInstituteId?: string;
    onInstituteChange?: (id: string) => void;
}

const ProfileAnalyticsDashboard = ({
    instituteId,
    data: dataProp,
    refreshKey,
    embedded,
    instituteOptions,
    selectedInstituteId,
    onInstituteChange,
}: Props) => {
    const [loading, setLoading] = useState(dataProp === undefined);
    const [data, setData] = useState<ProfileAnalyticsData | null>(dataProp ?? null);
    const [liveEntrepreneur, setLiveEntrepreneur] = useState<EntrepreneurAnalysisData | null>(null);
    const [liveEntrepreneurError, setLiveEntrepreneurError] = useState<string | undefined>();
    const [liveEntrepreneurLoading, setLiveEntrepreneurLoading] = useState(!embedded);

    const load = useCallback(async () => {
        if (!instituteId) return;
        setLoading(true);
        try {
            const res = embedded
                ? await ProfileAnalyticsService.getDashboard(instituteId)
                : await ProfileAnalyticsService.getSnapshotDashboard(instituteId);
            setData(res?.data ?? null);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [instituteId, embedded]);

    // If the caller passes data directly, use it; otherwise fetch.
    useEffect(() => {
        if (dataProp !== undefined) {
            setData(dataProp);
            setLoading(false);
            return;
        }
        load();
    }, [dataProp, load, refreshKey]);

    useEffect(() => {
        if (embedded || !instituteId) {
            setLiveEntrepreneur(null);
            setLiveEntrepreneurError(undefined);
            setLiveEntrepreneurLoading(false);
            return;
        }
        const schoolId = analysisSchoolIds(instituteId, selectedInstituteId, instituteOptions);
        if (!schoolId.length) {
            setLiveEntrepreneur(null);
            setLiveEntrepreneurLoading(false);
            return;
        }
        let active = true;
        setLiveEntrepreneurLoading(true);
        ProfileAnalyticsService.getEntrepreneurAnalysis(schoolId)
            .then((analysis) => {
                if (!active) return;
                setLiveEntrepreneur(analysis);
                setLiveEntrepreneurError(undefined);
            })
            .catch(() => {
                if (!active) return;
                setLiveEntrepreneur(null);
                setLiveEntrepreneurError('Could not load entrepreneur analysis.');
            })
            .finally(() => {
                if (active) setLiveEntrepreneurLoading(false);
            });
        return () => {
            active = false;
        };
    }, [embedded, instituteId, selectedInstituteId, instituteOptions]);

    if (loading) {
        return (
            <div className="text-center p-5 text-muted">
                <i className="icon-spinner spin font-22 me-2" aria-hidden="true"></i>
                Loading Profile Analytics…
            </div>
        );
    }

    const errors = data?.meta?.sectionErrors || {};
    const campusId = campusQueryValue(selectedInstituteId ?? (instituteOptions ? null : instituteId));
    const enableDirectoryLinks = !embedded;

    const inner = (
        <>
            <SeniorityFunnelSection
                data={data?.seniorityFunnel ?? null}
                error={errors.seniorityFunnel}
                emptyMessage={
                    embedded ? undefined : 'No seniority snapshot for this campus yet.'
                }
                campusId={campusId}
                enableDirectoryLinks={enableDirectoryLinks}
            />
            <EntrepreneurAnalysisSection
                data={embedded ? data?.entrepreneurAnalysis ?? null : liveEntrepreneur}
                error={embedded ? errors.entrepreneurAnalysis : liveEntrepreneurError}
                emptyMessage={
                    embedded
                        ? undefined
                        : liveEntrepreneurLoading
                          ? 'Loading entrepreneur analysis…'
                          : 'No entrepreneur analysis for this campus yet.'
                }
                campusId={campusId}
                enableDirectoryLinks={enableDirectoryLinks}
            />
            <BreakupSection
                title="Industry Wise Breakup"
                nameHeader="Industry"
                unit="industries"
                data={data?.industryBreakup ?? null}
                error={errors.industryBreakup}
                emptyMessage={embedded ? undefined : 'No industry snapshot for this campus yet.'}
                filterKind="industry"
                campusId={campusId}
                enableDirectoryLinks={enableDirectoryLinks}
            />
            <BreakupSection
                title="Functional Area Wise Breakup"
                nameHeader="Functional Area"
                unit="functions"
                data={data?.functionalArea ?? null}
                error={errors.functionalArea}
                emptyMessage={embedded ? undefined : 'No functional-area snapshot for this campus yet.'}
                filterKind="functionalArea"
                campusId={campusId}
                enableDirectoryLinks={enableDirectoryLinks}
            />
            {/* Email & Channel Engagement Rates — hidden for now.
                <EngagementRatesSection data={data?.engagementRates ?? null} error={errors.engagementRates} /> */}
        </>
    );

    if (embedded) return inner;

    return (
        <div className="main flex-1">
            <section className="profile-analytics-dashboard">
                <div className="container max-1140 px-lg-0">
                    <div className="d-flex justify-content-between align-items-center my-4 flex-wrap gap-2">
                        <h1 className="font-26 font-600 mb-0">Profile Analytics Dashboard</h1>
                        <div className="d-flex align-items-center flex-wrap gap-3">
                            {instituteOptions && instituteOptions.length > 1 && (
                                <div className="d-flex align-items-center">
                                    <label className="font-13 font-600 me-2 mb-0" style={{ color: '#6b7785' }}>
                                        Institute
                                    </label>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ maxWidth: 280 }}
                                        value={selectedInstituteId ?? ''}
                                        onChange={(e) => onInstituteChange && onInstituteChange(e.target.value)}>
                                        {instituteOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {/* {data?.meta?.fileName && (
                                <span className="font-12 text-muted">Source: {data.meta.fileName}</span>
                            )} */}
                        </div>
                    </div>
                    {inner}
                </div>
            </section>
        </div>
    );
};

export default ProfileAnalyticsDashboard;
